// Task #2117 — Ads Express run queue (global master-only `runQueue`
// beta flag). Shared by the standard and KC pages: a small panel listing the
// user's non-terminal runs in FIFO order ("Running" + "Queued #2, #3 …"),
// with per-run View (switch which run's results the page displays) and
// Cancel. Polling the /runs endpoint also self-heals the server-side queue
// (idempotent worker reattach with a FIFO guard), so a restart mid-queue
// resumes as soon as this panel is on screen.
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, X, Eye, ListOrdered } from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";

export interface AdRunSummary {
  id: string;
  status: "queued" | "running" | "completed" | "partial" | "failed" | "cancelled";
  createdAt: string | null;
  selectedAdUnitIds: string[] | null;
  personaNameSnapshot: string | null;
  productNameSnapshot: string | null;
  angleNameSnapshot: string | null;
  aspectRatio: string | null;
}

/**
 * Poll the user's running + queued runs (oldest first — queue order).
 * Polls every 4s while enabled; the endpoint doubles as the queue's
 * self-heal (worker reattach) so keep it enabled whenever the flag is on.
 */
export function useAdsExpressRunQueue(
  brandId: string | undefined,
  variant: "standard" | "kc",
  enabled: boolean,
) {
  return useQuery<{ runs: AdRunSummary[]; recent: AdRunSummary[] }>({
    queryKey: [`/api/brands/${brandId}/ad-generations/runs`, variant],
    queryFn: async () => {
      const res = await fetch(
        `/api/brands/${brandId}/ad-generations/runs?variant=${variant}`,
        { credentials: "include" },
      );
      if (!res.ok) throw new Error(`Failed to load runs (${res.status})`);
      return res.json();
    },
    enabled: !!brandId && enabled,
    refetchInterval: (q) => {
      const data = q.state.data as { runs?: AdRunSummary[] } | undefined;
      // Fast poll while anything is active; slow keep-alive otherwise so a
      // run queued from another tab still shows up.
      return data?.runs && data.runs.length > 0 ? 4000 : 15000;
    },
    refetchOnWindowFocus: true,
  });
}

export function invalidateAdsExpressRunQueue(brandId: string | undefined, variant: "standard" | "kc") {
  queryClient.invalidateQueries({ queryKey: [`/api/brands/${brandId}/ad-generations/runs`, variant] });
}

function runLabel(run: AdRunSummary): string {
  const parts: string[] = [];
  if (run.productNameSnapshot) parts.push(run.productNameSnapshot);
  if (run.personaNameSnapshot) parts.push(run.personaNameSnapshot);
  const n = run.selectedAdUnitIds?.length ?? 0;
  parts.push(`${n} template${n === 1 ? "" : "s"}`);
  return parts.join(" · ");
}

/**
 * Queue panel. Renders nothing when there are fewer than 1 non-terminal
 * runs (single-run flows keep today's UI untouched). Cancelling any run
 * only affects that run — the server promotes the next queued run when the
 * running one is cancelled.
 */
export function AdsExpressRunQueuePanel({
  brandId,
  variant,
  runs,
  activeGenerationId,
  onSelect,
  className,
}: {
  brandId: string;
  variant: "standard" | "kc";
  runs: AdRunSummary[];
  activeGenerationId: string | null;
  onSelect: (id: string) => void;
  className?: string;
}) {
  const { toast } = useToast();
  const [cancellingIds, setCancellingIds] = useState<Set<string>>(new Set());

  if (runs.length === 0) return null;

  const cancelRun = async (id: string) => {
    if (cancellingIds.has(id)) return;
    setCancellingIds((prev) => new Set(prev).add(id));
    try {
      await apiRequest("POST", `/api/brands/${brandId}/ad-generations/${id}/cancel`, {});
      invalidateAdsExpressRunQueue(brandId, variant);
      queryClient.invalidateQueries({ queryKey: [`/api/ad-generations`, id] });
    } catch (e: unknown) {
      toast({
        title: "Couldn't cancel run",
        description: e instanceof Error ? e.message : String(e),
        variant: "destructive",
      });
    } finally {
      setCancellingIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
    }
  };

  return (
    <Card className={cn("p-3", className)} data-testid="ads-express-run-queue">
      <div className="flex items-center gap-2 mb-2">
        <ListOrdered className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-semibold">Run queue</span>
        <span className="text-xs text-muted-foreground">
          {runs.length} active run{runs.length === 1 ? "" : "s"}
        </span>
      </div>
      <div className="space-y-1.5">
        {runs.map((run, i) => {
          const isViewing = run.id === activeGenerationId;
          const isQueued = run.status === "queued";
          return (
            <div
              key={run.id}
              className={cn(
                "flex items-center gap-2 rounded-md border px-2.5 py-1.5",
                isViewing ? "border-primary/50 bg-primary/5" : "border-border",
              )}
              data-testid={`run-queue-row-${i}`}
            >
              {isQueued ? (
                <Badge variant="secondary" className="shrink-0 tabular-nums">
                  Queued #{i + 1}
                </Badge>
              ) : (
                <Badge className="shrink-0">
                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  Running
                </Badge>
              )}
              <span className="text-xs text-muted-foreground truncate flex-1 min-w-0">
                {runLabel(run)}
              </span>
              {!isViewing && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-xs"
                  onClick={() => onSelect(run.id)}
                  data-testid={`button-view-run-${i}`}
                >
                  <Eye className="h-3.5 w-3.5 mr-1" />
                  View
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                onClick={() => cancelRun(run.id)}
                disabled={cancellingIds.has(run.id)}
                data-testid={`button-cancel-run-${i}`}
              >
                {cancellingIds.has(run.id) ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <X className="h-3.5 w-3.5" />
                )}
              </Button>
            </div>
          );
        })}
      </div>
    </Card>
  );
}
